'use client'

import { Clock24, PlusSquareOutlined24, Truck24, X24 } from '@vinted/monochrome-icons'
import { Badge, Button, Cell, Divider, Icon, Image, Navigation, Spacer, Text } from '@vinted/web-ui'
import { compact, noop } from 'lodash'
import { Fragment, useEffect, useState } from 'react'

import { ModeratedItemCell } from '@marketplace-web/domain/conversation-moderation'
import {
  CarrierChangeModal,
  ExtendShippingDeadlineModal,
} from '@marketplace-web/conversation-shipping/conversation-shipping-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { StartBundleButtonWrapper } from '@marketplace-web/bundles/items-bundle-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { UserImage } from '@marketplace-web/common-components/user-image-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { isDateInTheFuture, urlWithParams } from '@marketplace-web/shared/utils'
import { OrderActions } from '@marketplace-web/domain/conversation-order-actions'
import {
  getSnadComplaint,
  transformSnadComplaintDto,
} from '@marketplace-web/complaints/complaints-data'
import {
  AdditionalTransactionAction,
  TransactionItemModel,
  conversationDetailsItemClick,
  conversationDetailsUserNameClick,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { clickEvent } from '_libs/common/event-tracker/events'
import { CREATE_BUNDLE_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import { ConversationActions } from '../ConversationActions'
import { getConversationIdFromUrl } from '../../utils'

type Props = {
  toggleSidebar?: () => void
}

const ConversationDetails = ({ toggleSidebar = noop }: Props) => {
  const { relativeUrl } = useLocation()
  const { transaction, conversation } = useConversationContext()
  const { user } = useSession()

  const translate = useTranslate()
  const { track } = useTracking()
  const inboxTranslate = useTranslate('inbox')
  const infoTranslate = useTranslate('conversation.conversation_info')
  const [isExtendShippingDeadlineModalOpen, setIsExtendShippingDeadlineModalOpen] = useState(false)
  const [isCarrierChangeModalOpen, setIsCarrierChangeModalOpen] = useState(false)

  const oppositeUser = conversation?.oppositeUser
  const moderatedItems = conversation?.moderatedItems
  const transactionId = conversation?.transactionId
  const conversationId = conversation?.id
  const complaintId = transaction?.complaintId
  const hideOrderDetailsAction = conversation?.orderDetailsVariant === 'c'

  const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)

  const removeItemInfoFromConversationDetailsABTest = useAbTest(
    'remove_item_info_from_conversation_details',
  )
  useTrackAbTest(removeItemInfoFromConversationDetailsABTest)

  const {
    fetch: fetchSnadComplaint,
    transformedData: complaint,
    isLoading,
  } = useFetch(getSnadComplaint, transformSnadComplaintDto)

  useEffect(() => {
    if (!complaintId || !transactionId) return

    fetchSnadComplaint(transactionId)
  }, [complaintId, transactionId, fetchSnadComplaint])

  const trackExtendShippingDeadlineClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: ClickableElement.ExtendShippingDeadline,
        screen: Screen.OrderDetails,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const trackCarrierChangeClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: ClickableElement.RequestCarrierChange,
        screen: Screen.OrderDetails,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleExtendShippingDeadlineClick = () => {
    trackExtendShippingDeadlineClick()
    setIsExtendShippingDeadlineModalOpen(true)
  }

  const handleExtendShippingDeadlineModalClose = () => {
    setIsExtendShippingDeadlineModalOpen(false)
  }

  const handleCarrierChangeClick = () => {
    trackCarrierChangeClick()
    setIsCarrierChangeModalOpen(true)
  }

  const handleCarrierChangeModalClose = () => {
    setIsCarrierChangeModalOpen(false)
  }

  const handleCarrierChangeModalSubmit = () => {
    toggleSidebar()
  }

  const renderAvailableDiscount = () => {
    const availableDiscount = transaction?.seller?.bundleDiscount

    if (!availableDiscount) return translate('bundles.discounts.not_available')

    const { discounts } = availableDiscount

    if (!discounts || !availableDiscount.isEnabled) {
      return translate('bundles.discounts.not_available')
    }

    const maxDiscount = Math.max(...discounts.map(element => Number(element.fraction))) * 100
    const maxItems = Math.max(...discounts.map(element => element.minimalItemCount))

    return translate('bundles.discounts.available', {
      availableDiscount: maxDiscount,
      count: maxItems,
    })
  }

  const renderAddMoreItemsAction = () => {
    if (!transaction?.seller || isSellerCountdownActive) return null

    const addMoreBundleItemsUrl = urlWithParams(CREATE_BUNDLE_URL(transaction.seller.id), {
      transaction_id: transaction.id,
      item_ids: transaction.order.itemIds,
    })

    return (
      <StartBundleButtonWrapper url={addMoreBundleItemsUrl} screen={Screen.OrderDetails}>
        {({ onClick }) => (
          <Cell
            chevron
            title={infoTranslate('edit_order')}
            body={renderAvailableDiscount()}
            prefix={<Icon name={PlusSquareOutlined24} color={Icon.Color.Primary} />}
            type={Cell.Type.Navigating}
            onClick={onClick}
            testId="conversation-info-add-more-items-cell"
          />
        )}
      </StartBundleButtonWrapper>
    )
  }

  const renderOrderItemDetails = (item: TransactionItemModel) => {
    const itemDescriptionSeparator = ' • '
    const itemInfo = compact([item.size, item.status]).join(itemDescriptionSeparator)

    const complaintOrderItem = complaint?.complaintOrderItems?.find(
      complaintItem => complaintItem.item.id === item.id,
    )
    const shouldItemInfoBeShown = removeItemInfoFromConversationDetailsABTest?.variant !== 'on'

    return (
      <>
        {shouldItemInfoBeShown && (
          <>
            <Text as="span" text={itemInfo} testId="conversation-item-info" />
            <Spacer size={Spacer.Size.Small} />
          </>
        )}
        {complaintOrderItem?.reasonText && (
          <Badge
            content={complaintOrderItem?.reasonText}
            theme="warning"
            styling={Badge.Styling.Light}
          />
        )}
      </>
    )
  }

  const handleOrderItemClick = () => {
    if (!transactionId || !conversationId) return

    track(
      conversationDetailsItemClick({
        transactionId,
        conversationId,
        userSide: transaction?.userSide ?? null,
        oppositeUserId: oppositeUser?.id ?? null,
      }),
    )
  }

  const renderOrderItems = () => {
    if (!transaction) return null

    if (complaintId && isLoading) return <ContentLoader testId="conversation-info-loader" />

    const {
      order: { items },
      availableActions,
    } = transaction

    return (
      <>
        {items?.map(item => (
          <Fragment key={item.id}>
            <Cell
              type={Cell.Type.Navigating}
              chevron
              title={item.title}
              body={renderOrderItemDetails(item)}
              onClick={handleOrderItemClick}
              testId="conversation-item"
              prefix={
                <Image
                  src={item.thumbnailUrl}
                  size={Image.Size.Large}
                  styling={Image.Styling.Rounded}
                />
              }
              url={item.url}
            />
            <Divider />
          </Fragment>
        ))}
        {availableActions?.includes(AdditionalTransactionAction.Bundle) &&
          renderAddMoreItemsAction()}
      </>
    )
  }

  const renderOrderActions = () => {
    return <OrderActions transaction={transaction} oppositeUser={oppositeUser} />
  }

  const handleUsernameClick = () => {
    if (!conversationId || !transactionId) return

    track(
      conversationDetailsUserNameClick({
        conversationId,
        transactionId,
        userSide: transaction?.userSide ?? null,
        oppositeUserId: oppositeUser?.id ?? null,
      }),
    )
  }

  const renderOppositeUser = () => {
    if (!oppositeUser) return null

    const { login, profileUrl, photoThumbnail, isSystem } = oppositeUser

    return (
      <>
        <Cell
          title={login}
          prefix={
            <UserImage
              src={photoThumbnail}
              size={Image.Size.Medium}
              styling={Image.Styling.Circle}
              isSystem={isSystem}
              testId="opposite-user-image"
            />
          }
          url={profileUrl}
          onClick={handleUsernameClick}
          type={Cell.Type.Navigating}
        />
        <Divider />
      </>
    )
  }

  const renderConversationActions = () => {
    return (
      <ConversationActions
        transaction={transaction}
        conversationId={getConversationIdFromUrl(relativeUrl)}
        oppositeUser={oppositeUser}
        userId={user?.id}
      />
    )
  }

  const renderExtendShippingDeadlineAction = () => {
    if (hideOrderDetailsAction) return null

    const isExtendDeadlineActionExist = transaction?.availableActions.includes(
      AdditionalTransactionAction.ExtendShippingDeadline,
    )

    if (!isExtendDeadlineActionExist || !transaction) return null

    return (
      <>
        <Cell
          title={infoTranslate('extend_shipping_deadline')}
          prefix={<Icon name={Clock24} color={Icon.Color.Primary} />}
          type={Cell.Type.Navigating}
          testId="conversation-info-extend-shipping-deadline"
          onClick={handleExtendShippingDeadlineClick}
        />
        <Divider />
      </>
    )
  }

  const renderChangeDeliveryOptionAction = () => {
    if (hideOrderDetailsAction) return null

    const isChangeDeliveryOptionExist = transaction?.availableActions.includes(
      AdditionalTransactionAction.ChangeDeliveryOption,
    )

    if (!isChangeDeliveryOptionExist || !transaction) return null

    return (
      <>
        <Cell
          title={infoTranslate('change_delivery_option')}
          prefix={<Icon name={Truck24} color={Icon.Color.Primary} />}
          type={Cell.Type.Navigating}
          testId="conversation-info-change-delivery-option"
          onClick={handleCarrierChangeClick}
        />
        <Divider />
      </>
    )
  }

  const renderModeratedItems = () => {
    if (!moderatedItems) return null

    return (
      <>
        <SeparatedList separator={<Divider />}>
          {moderatedItems.items.map(item => (
            <ModeratedItemCell item={item} key={item.id} isSingleItem />
          ))}
        </SeparatedList>
        <Divider />
      </>
    )
  }

  const renderExtendShippingDeadlineModal = () => {
    return (
      <ExtendShippingDeadlineModal
        isOpen={isExtendShippingDeadlineModalOpen}
        onClose={handleExtendShippingDeadlineModalClose}
      />
    )
  }

  const renderCarrierChangeModal = () => {
    return (
      <CarrierChangeModal
        show={isCarrierChangeModalOpen}
        onClose={handleCarrierChangeModalClose}
        onSubmit={handleCarrierChangeModalSubmit}
      />
    )
  }

  return (
    <>
      <Navigation
        body={
          <Cell styling={Cell.Styling.Tight}>
            <Text
              as="h2"
              text={infoTranslate('details')}
              type={Text.Type.Title}
              theme="amplified"
            />
          </Cell>
        }
        right={
          <Button
            styling={Button.Styling.Flat}
            theme="amplified"
            inline
            iconName={X24}
            onClick={toggleSidebar}
            aria={{
              'aria-label': inboxTranslate('a11y.close'),
            }}
            testId="conversation-info-close-button"
          />
        }
      />
      <Divider />
      <div className="thread__conversation-info">
        {renderOrderItems()}
        {renderModeratedItems()}
        {renderExtendShippingDeadlineAction()}
        {renderChangeDeliveryOptionAction()}
        {renderOrderActions()}
        {renderOppositeUser()}
        {renderConversationActions()}
        <Spacer size={Spacer.Size.Large} />
      </div>
      {renderExtendShippingDeadlineModal()}
      {renderCarrierChangeModal()}
    </>
  )
}

export default ConversationDetails
